/**
 * Environmental Geotech — Temporal Analysis  (Step 2 of the 8-step intelligence loop)
 *
 * Select a protected zone and date range, then fetch Sentinel-2 satellite imagery.
 * Displays NDVI (canopy cover), NDWI (wetland moisture), NBR (burn ratio), and LST
 * over time to detect deforestation, illegal clearing, wetland encroachment, or wildfire.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Satellite, RefreshCw, BarChart2, CloudOff, TreePine } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { satelliteBoundariesApi, satelliteImageryApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ZoneBoundary {
  id: string;
  name: string;
  area_hectares?: number;
  metadata?: { zone_type?: string; vertical?: string };
}

interface VegetationIndex {
  index_type: string;
  mean_value: number;
  min_value: number;
  max_value: number;
  std_dev: number;
}

interface ImageryRecord {
  id: string;
  acquisition_date: string;
  satellite: string;
  cloud_cover_percentage: number;
  indices: VegetationIndex[];
}

// ─── Index colour & descriptions ──────────────────────────────────────────────

const INDEX_COLOUR: Record<string, string> = {
  NDVI:  'text-green-700 dark:text-green-400',
  NDWI:  'text-cyan-700 dark:text-cyan-400',
  NBR:   'text-orange-700 dark:text-orange-400',
  LST:   'text-red-700 dark:text-red-400',
};

const INDEX_DESCRIPTIONS: Record<string, string> = {
  NDVI: 'Normalised Difference Vegetation Index — canopy cover health; declining values indicate deforestation or clearing',
  NDWI: 'Normalised Difference Water Index — wetland moisture; falling values signal wetland drainage or encroachment',
  NBR:  'Normalised Burn Ratio — post-fire severity; strongly negative values confirm burn scars',
  LST:  'Land Surface Temperature — thermal anomalies from canopy loss or exposed soil',
};

const INDEX_HEADERS = ['NDVI', 'NDWI', 'NBR', 'LST'];

// ─── Component ────────────────────────────────────────────────────────────────

export const EnvTemporalAnalysisPage: React.FC = () => {
  const [zones,    setZones]    = useState<ZoneBoundary[]>([]);
  const [imagery,  setImagery]  = useState<ImageryRecord[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState<string | null>(null);

  const [selectedZone, setSelectedZone] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Load environmental zones
  useEffect(() => {
    satelliteBoundariesApi.getAll()
      .then((res) => {
        const all: ZoneBoundary[] = res.data?.boundaries ?? [];
        setZones(all.filter((b) => b.metadata?.vertical === 'environmental'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadImagery = useCallback(async () => {
    if (!selectedZone) { setImagery([]); return; }
    setFetching(true); setError(null);
    try {
      const res = await satelliteImageryApi.getImagery(selectedZone, { startDate, endDate });
      setImagery(res.imagery ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load imagery');
    } finally {
      setFetching(false);
    }
  }, [selectedZone, startDate, endDate]);

  useEffect(() => { loadImagery(); }, [loadImagery]);

  const handleFetch = async () => {
    if (!selectedZone) { setError('Select a zone boundary first'); return; }
    setFetching(true); setError(null);
    try {
      await satelliteImageryApi.fetchImagery(selectedZone, { startDate, endDate });
      setSuccess('Satellite fetch queued — results appear within 2–3 minutes');
      await loadImagery();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Fetch failed');
    } finally {
      setFetching(false);
    }
  };

  const selectedName = zones.find((z) => z.id === selectedZone)?.name;
  const INPUT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Clock className="w-7 h-7 text-green-600" /> Temporal Analysis
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Step 2 — Zone satellite timeline: NDVI canopy cover, NDWI wetland moisture, NBR burn index, LST
        </p>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Controls ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-end">
          {loading ? (
            <Spinner size="sm" />
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Protected Zone</label>
              <select className={INPUT_CLS} value={selectedZone} onChange={(e) => setSelectedZone(e.target.value)}>
                <option value="">Select zone…</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}{z.metadata?.zone_type ? ` (${z.metadata.zone_type.replace(/_/g, ' ')})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Start Date</label>
            <input type="date" className={INPUT_CLS} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">End Date</label>
            <input type="date" className={INPUT_CLS} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button onClick={handleFetch} loading={fetching} disabled={!selectedZone}>
            <Satellite className="w-4 h-4 mr-1" /> Fetch Imagery
          </Button>
          <Button variant="outline" onClick={loadImagery} disabled={!selectedZone || fetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </Card>

      {/* ── Index descriptions ── */}
      {selectedZone && (
        <Card padding="sm">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Environmental Index Guide</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(INDEX_DESCRIPTIONS).map(([idx, desc]) => (
              <div key={idx} className="flex gap-2">
                <span className={`text-xs font-mono font-bold w-10 flex-shrink-0 ${INDEX_COLOUR[idx]}`}>{idx}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{desc}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Results ── */}
      {!selectedZone ? (
        <Card>
          <div className="text-center py-10">
            <BarChart2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No zone selected</p>
            <p className="text-sm text-gray-400 mt-1">Select a protected zone above to view its satellite timeline</p>
          </div>
        </Card>
      ) : fetching ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : imagery.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <CloudOff className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No imagery available for {selectedName}</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>Fetch Imagery</strong> to queue a Sentinel-2 acquisition</p>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {imagery.length} satellite acquisition{imagery.length !== 1 ? 's' : ''} — {selectedName}
            </p>
            <TreePine className="w-4 h-4 text-green-500" />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Date', 'Satellite', 'Cloud Cover', ...INDEX_HEADERS].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {imagery.map((rec) => {
                  const idx = (type: string) => rec.indices.find((i) => i.index_type === type);
                  return (
                    <tr key={rec.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {new Date(rec.acquisition_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{rec.satellite}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {rec.cloud_cover_percentage != null ? Number(rec.cloud_cover_percentage).toFixed(1) : '—'}%
                      </td>
                      {INDEX_HEADERS.map((type) => {
                        const i = idx(type);
                        return (
                          <td key={type} className={`px-4 py-3 text-sm font-mono ${INDEX_COLOUR[type] ?? ''}`}>
                            {i ? Number(i.mean_value).toFixed(3) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default EnvTemporalAnalysisPage;

// Made with Bob
